(() => {
  'use strict';
  if (window.csWorldHeroStory) {
    window.csWorldHeroStory.initialize();
    return;
  }
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const completed = new Set();
  const controllers = new Map();
  const consentNodes = new Map();
  const duration = 4600;
  let consentOpen = true;
  let privacyFrame = null;
  function refreshPrivacy() {
    privacyFrame = null;
    for (const [node, observer] of consentNodes) {
      if (!node.isConnected) {
        observer.disconnect();
        consentNodes.delete(node);
      }
    }
    const nodes = [...document.querySelectorAll('#shopify-pc__banner,#shopify-pc__prefs')];
    nodes.forEach(node => {
      if (consentNodes.has(node)) return;
      const observer = new MutationObserver(schedulePrivacyRefresh);
      observer.observe(node, { attributes: true, childList: true, subtree: true, attributeFilter: ['hidden', 'style', 'class', 'aria-hidden', 'open'] });
      consentNodes.set(node, observer);
    });
    consentOpen = nodes.some(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
    controllers.forEach(controller => controller.sync());
  }
  function schedulePrivacyRefresh() {
    if (privacyFrame === null) privacyFrame = requestAnimationFrame(refreshPrivacy);
  }
  const insertionObserver = new MutationObserver(schedulePrivacyRefresh);
  insertionObserver.observe(document.body, { childList: true });
  function mount(hero) {
    const id = hero.dataset.csHeroStory;
    const character = hero.querySelector('[data-story-sprite]');
    const frame = character?.querySelector('.cw-hero__story-frame');
    const staticCharacter = hero.querySelector('[data-cs-world-character="microbe"]');
    const landscape = hero.querySelector('.cw-hero__landscape');
    if (!character || !frame || !staticCharacter || !landscape) return;
    let visible = false;
    let painted = false;
    let ready = false;
    let finished = false;
    let destroyed = false;
    let requested = false;
    let elapsed = 0;
    let previous = null;
    let raf = null;
    let paintFrame = null;
    let idle = null;
    let sprite = null;
    let currentPhase = 'welcome';
    function cancelFrame() {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
      previous = null;
    }
    function cancelIdle() {
      if (idle === null) return;
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
      idle = null;
    }
    function phase(value) {
      if (currentPhase === value) return;
      currentPhase = value;
      hero.dataset.storyPhase = value;
    }
    function settle(staticOnly = false) {
      cancelFrame();
      cancelIdle();
      finished = true;
      completed.add(id);
      phase('settled');
      observer?.disconnect();
      if (staticOnly) {
        character.hidden = true;
        delete hero.dataset.csStoryReady;
      }
    }
    function eligible() {
      return !destroyed && !finished && hero.isConnected && painted && visible && !document.hidden && !reduced.matches && !consentOpen;
    }
    function update(now) {
      raf = null;
      if (!eligible() || !ready) { sync(); return; }
      if (previous !== null) elapsed += now - previous;
      previous = now;
      if (elapsed >= duration) { settle(); return; }
      if (elapsed >= 3300) phase('guide');
      else if (elapsed >= 1100) phase('soil');
      raf = requestAnimationFrame(update);
    }
    function requestSprite() {
      idle = null;
      if (!eligible() || requested) return;
      requested = true;
      sprite = new Image();
      sprite.decoding = 'async';
      sprite.fetchPriority = 'low';
      sprite.onload = async () => {
        try { await sprite.decode(); } catch { if (!destroyed) settle(true); return; }
        if (destroyed || !hero.isConnected) return;
        if (reduced.matches || sprite.naturalWidth !== 1800 || sprite.naturalHeight !== 768) { settle(true); return; }
        frame.style.backgroundImage = `url("${character.dataset.storySprite}")`;
        ready = true;
        sync();
      };
      sprite.onerror = () => settle(true);
      sprite.src = character.dataset.storySprite;
    }
    function sync() {
      if (destroyed) return;
      if (reduced.matches || completed.has(id)) { settle(reduced.matches || !ready); return; }
      if (!eligible()) {
        cancelFrame();
        cancelIdle();
        return;
      }
      if (!requested && idle === null) {
        idle = 'requestIdleCallback' in window ? window.requestIdleCallback(requestSprite, { timeout: 1500 }) : setTimeout(requestSprite, 0);
      }
      if (ready && raf === null) {
        character.hidden = false;
        hero.dataset.csStoryReady = '';
        raf = requestAnimationFrame(update);
      }
    }
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .45);
      sync();
    }, { threshold: [0, .45] }) : null;
    async function landscapeReady() {
      landscape.removeEventListener('load', landscapeReady);
      landscape.removeEventListener('error', landscapeFailed);
      try { await landscape.decode(); } catch { if (!destroyed) settle(true); return; }
      if (destroyed || finished || !hero.isConnected) return;
      paintFrame = requestAnimationFrame(() => {
        paintFrame = requestAnimationFrame(() => {
          paintFrame = null;
          painted = true;
          sync();
        });
      });
    }
    function landscapeFailed() {
      landscape.removeEventListener('load', landscapeReady);
      settle(true);
    }
    controllers.set(hero, {
      sync,
      destroy() {
        destroyed = true;
        observer?.disconnect();
        cancelFrame();
        cancelIdle();
        if (paintFrame !== null) cancelAnimationFrame(paintFrame);
        landscape.removeEventListener('load', landscapeReady);
        landscape.removeEventListener('error', landscapeFailed);
        if (sprite) { sprite.onload = null; sprite.onerror = null; }
      }
    });
    if (!observer || reduced.matches || completed.has(id)) { settle(true); return; }
    observer.observe(staticCharacter);
    if (landscape.complete) {
      if (landscape.naturalWidth > 0) landscapeReady();
      else landscapeFailed();
    } else {
      landscape.addEventListener('load', landscapeReady, { once: true });
      landscape.addEventListener('error', landscapeFailed, { once: true });
    }
  }
  function initialize() {
    for (const [hero, controller] of controllers) {
      if (!hero.isConnected) {
        controller.destroy();
        controllers.delete(hero);
      }
    }
    document.querySelectorAll('[data-cs-hero-story]').forEach(hero => {
      if (!controllers.has(hero)) mount(hero);
    });
    schedulePrivacyRefresh();
  }
  window.csWorldHeroStory = { initialize };
  reduced.addEventListener('change', () => controllers.forEach(controller => controller.sync()));
  document.addEventListener('visibilitychange', () => controllers.forEach(controller => controller.sync()));
  document.addEventListener('visitorConsentCollected', schedulePrivacyRefresh);
  document.addEventListener('click', schedulePrivacyRefresh, { passive: true });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    for (const [hero, controller] of controllers) {
      if (!hero.isConnected || event?.target?.contains(hero)) {
        controller.destroy();
        controllers.delete(hero);
      }
    }
  });
  initialize();
})();
